import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { SvgIcon } from "../components/SvgIcon";
import { BtnSpinner } from "../components/BtnSpinner";

type SignupResp = { ok: true; email: string };

function getPasswordStrengthScore(pw: string): number {
  const p = pw ?? "";
  if (p.length == 0) return 0;

  const hasLower = /[a-z]/.test(p);
  const hasUpper = /[A-Z]/.test(p);
  const hasDigit = /[0-9]/.test(p);
  const hasSymbol = /[^a-zA-Z0-9]/.test(p);
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  let score = 0;
  if (p.length >= 8) score += 1;
  if (p.length >= 12) score += 1;
  if (variety >= 3) score += 1;
  if (p.length >= 16 && variety >= 3) score += 1;

  if (score < 0) score = 0;
  if (score > 4) score = 4;
  return score;
}

function strengthLabelKey(score: number): string {
  if (score <= 1) return "signup.pw.weak";
  if (score == 2) return "signup.pw.ok";
  if (score == 3) return "signup.pw.good";
  return "signup.pw.strong";
}

function normalizeLocalPart(s: string): string {
  return s.trim().toLowerCase();
}

function isValidLocalPart(s: string): boolean {
  // Keep v1 strict to avoid weird edge cases; we can relax later.
  // Allowed: letters, digits, dot, underscore, hyphen. Must start/end with alnum.
  if (s.length < 1 || s.length > 64) return false;
  if (!/^[a-z0-9][a-z0-9._-]*[a-z0-9]$/.test(s)) return false;
  if (s.includes("..")) return false;
  return true;
}

export function SignupView(props: { locale: Locale; onAuthed: (email: string) => void }) {
  const [localPart, setLocalPart] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Username-specific error (reserved name, already taken) — rendered inline
  // next to the localpart field so it reads as field validation, not a form error.
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [availabilityStatus, setAvailabilityStatus] = useState<"idle" | "checking" | "available" | "unavailable">("idle");
  const [availabilityText, setAvailabilityText] = useState<string>("");
  const [availabilityReqId, setAvailabilityReqId] = useState<number>(0);

  const pwScore = useMemo(() => getPasswordStrengthScore(password), [password]);
  const pwPct = useMemo(() => String(Math.round((pwScore / 4) * 100)), [pwScore]);

  const email = useMemo(() => `${normalizeLocalPart(localPart)}@kveja.com`, [localPart]);

  useEffect(() => {
    const lp = normalizeLocalPart(localPart);
    // Only check when username looks valid enough
    if (!isValidLocalPart(lp)) {
      setAvailabilityStatus("idle");
      setAvailabilityText("");
      return;
    }

    setAvailabilityStatus("checking");
    setAvailabilityText(t(props.locale, "signup.checkingAvailability"));

    const reqId = availabilityReqId + 1;
    setAvailabilityReqId(reqId);

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const q = encodeURIComponent(email);
        const res = await apiGet<{ ok: true; available: boolean; reason?: string; message?: string }>(
          `/v1/availability?email=${q}`
        );
        // Ignore outdated responses
        if (reqId !== availabilityReqId + 1) return;
        if (!res.ok) {
          setAvailabilityStatus("idle");
          setAvailabilityText("");
          return;
        }
        if (res.data.available) {
          setAvailabilityStatus("available");
          setAvailabilityText(t(props.locale, "signup.available"));
        } else {
          setAvailabilityStatus("unavailable");
          setAvailabilityText(t(props.locale, "signup.notAvailable"));
        }
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        setAvailabilityStatus("idle");
        setAvailabilityText("");
      }
    }, 450);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPart, email, props.locale]);

  const canSubmit = useMemo(() => {
    const lp = normalizeLocalPart(localPart);
    const lpOk = isValidLocalPart(lp);
    const pOk = password.length >= 8;
    const mOk = password === password2;
    return lpOk && pOk && mOk;
  }, [localPart, password, password2]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUsernameError(null);
    setBusy(true);
    try {
      const lp = normalizeLocalPart(localPart);
      if (!isValidLocalPart(lp)) {
        setError(t(props.locale, "signup.invalidLocalPart"));
        return;
      }

      // Backend expects username (local part), password, and confirmPassword
      const reg = await apiPost<SignupResp>("/v1/signup", {
        username: lp,
        password,
        confirmPassword: password2
      });
      if (!reg.ok) {
        // 409 responses are always username-level problems (reserved or taken);
        // render them inline next to the username field.
        if (reg.status === 409) {
          setUsernameError(reg.error);
        } else {
          setError(reg.error);
        }
        return;
      }

      // Signup already creates a session cookie — no separate login needed
      props.onAuthed(reg.data.email);
      window.history.pushState({}, "", "/inbox");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authShell">
      <div className="authBg" />
      <div className="authWrap">
        <a className="authBrand" href="/" aria-label="KVEJA home">
          <img className="authLogo" src="/brand/kveja_logo.png" alt="KVEJA" />
        </a>

        <div className="authCard">
          <div className="authHeader">
            <h1 className="authTitle">{t(props.locale, "signup.title")}</h1>
            <p className="authSubtitle">{t(props.locale, "signup.subtitle")}</p>
          </div>

          <form className="authForm" onSubmit={onSubmit}>
            <div className="authField">
              <div className="authLabelRow">
  <div className="authLabel">{t(props.locale, "signup.username")}</div>
  {availabilityStatus === "checking" ? (
    <div className="avail availChecking"><span className="spinner" /> {availabilityText}</div>
  ) : availabilityStatus === "available" ? (
    <div className="avail availOk"><span style={{ color: "var(--success, #10b981)" }}><SvgIcon name="check-circle" title="Valid" /></span> {availabilityText}</div>
  ) : availabilityStatus === "unavailable" ? (
    <div className="avail availBad"><span style={{ color: "var(--danger, #ef4444)" }}><SvgIcon name="x-circle" title="Invalid" /></span> {availabilityText}</div>
  ) : null}
</div>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  className="authInput"
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value)}
                  type="text"
                  autoComplete="username"
                  inputMode="text"
                  placeholder={t(props.locale, "signup.usernamePlaceholder")}
                  required
                  style={{ flex: 1 }}
                />
                <div className="small" style={{ whiteSpace: "nowrap" }}>
                  @kveja.com
                </div>
              </div>

              <div className="small">{t(props.locale, "signup.customDomainLater")}</div>
              {usernameError && (
                <div className="authFieldError" style={{ color: "var(--danger, #ef4444)", fontSize: "12px", marginTop: "4px" }}>
                  {usernameError}
                </div>
              )}
            </div>

            <div className="authField">
              <div className="authLabel">{t(props.locale, "signup.password")}</div>
              <input
                className="authInput"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
                required
              />
            </div>

            <div className="pwStrengthRow" aria-live="polite">
              <div className="pwStrengthTop">
                <div className="small">{t(props.locale, "signup.passwordStrength")}</div>
                <div className="small"><span className="pwStrengthLabel">{t(props.locale, strengthLabelKey(pwScore))}</span></div>
              </div>
              <div className="pwMeter" role="meter" aria-valuemin={0} aria-valuemax={4} aria-valuenow={pwScore}>
                <div className="pwMeterFill" style={{ width: `${pwPct}%` }} />
              </div>
            </div>

            <div className="authField">
              <div className="authLabel">{t(props.locale, "signup.password2")}</div>
              <input
                className="authInput"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                type="password"
                autoComplete="new-password"
                placeholder="Repeat password"
                required
              />
            </div>

            {error ? <div className="authError">{error}</div> : null}

            <div className="authRow authRowBottom">
              <button className="btn primary authBtn" disabled={!canSubmit || busy} type="submit">
                <BtnSpinner loading={busy}>{busy ? t(props.locale, "signup.submitting") : t(props.locale, "signup.submit")}</BtnSpinner>
              </button>
            </div>

            <div className="authFooter">
              <a className="small" href="/login">{t(props.locale, "signup.backToLogin")}</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
